import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ServicesPage = lazy(() => import('../pages/services/page'));
const ChatGPTAdsPage = lazy(() => import('../pages/chatgpt-ads/page'));
const GEOPage = lazy(() => import('../pages/geo/page'));
const VoiceAIPage = lazy(() => import('../pages/voice-ai/page'));
const IndustriesPage = lazy(() => import('../pages/industries/page'));
const CaseStudiesPage = lazy(() => import('../pages/case-studies/page'));
const ResourcesPage = lazy(() => import('../pages/resources/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const AuditPage = lazy(() => import('../pages/audit/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const ServiceCityPage = lazy(() => import('../pages/service-city/page'));
const ServiceAreasPage = lazy(() => import('../pages/service-areas/page'));
const MiamiPage = lazy(() => import('../pages/location/miami/page'));
const FortLauderdalePage = lazy(() => import('../pages/location/fort-lauderdale/page'));
const PalmBeachPage = lazy(() => import('../pages/location/palm-beach/page'));
const CalculatorsIndexPage = lazy(() => import('../pages/calculators/page'));
const MissedCallsCalculatorPage = lazy(() => import('../pages/calculator/missed-calls/page'));
const AiReceptionistRoiCalculatorPage = lazy(() => import('../pages/calculator/ai-receptionist-roi/page'));
const LeadValueCalculatorPage = lazy(() => import('../pages/calculator/lead-value/page'));
const SeoTrafficValueCalculatorPage = lazy(() => import('../pages/calculator/seo-traffic-value/page'));
const ReviewGenerationRoiCalculatorPage = lazy(() => import('../pages/calculator/review-generation-roi/page'));
const NotFound = lazy(() => import('../pages/NotFound'));

// ChatGPT Ads topical-authority cluster
const ClusterHubPage = lazy(() => import('../pages/cluster/hub/page'));
const ClusterVerticalPage = lazy(() => import('../pages/cluster/vertical/page'));
const ClusterLocationPage = lazy(() => import('../pages/cluster/location/page'));
const ClusterContentPage = lazy(() => import('../pages/cluster/content/page'));
const ClusterBlogPage = lazy(() => import('../pages/cluster/blog/page'));

// Per-prospect flywheel explainer microsite
const FlywheelPage = lazy(() => import('../pages/flywheel/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/services',
    element: <ServicesPage />,
  },
  {
    path: '/chatgpt-ads',
    element: <ChatGPTAdsPage />,
  },
  {
    path: '/geo',
    element: <GEOPage />,
  },
  {
    path: '/voice-ai',
    element: <VoiceAIPage />,
  },
  {
    path: '/industries',
    element: <IndustriesPage />,
  },
  {
    path: '/case-studies',
    element: <CaseStudiesPage />,
  },
  {
    path: '/resources',
    element: <ResourcesPage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/audit',
    element: <AuditPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/service-areas',
    element: <ServiceAreasPage />,
  },
  {
    path: '/miami',
    element: <MiamiPage />,
  },
  {
    path: '/fort-lauderdale',
    element: <FortLauderdalePage />,
  },
  {
    path: '/palm-beach',
    element: <PalmBeachPage />,
  },
  {
    path: '/services/:service/:city',
    element: <ServiceCityPage />,
  },
  {
    path: '/calculators',
    element: <CalculatorsIndexPage />,
  },
  {
    path: '/calculator/missed-calls',
    element: <MissedCallsCalculatorPage />,
  },
  {
    path: '/calculator/ai-receptionist-roi',
    element: <AiReceptionistRoiCalculatorPage />,
  },
  {
    path: '/calculator/lead-value',
    element: <LeadValueCalculatorPage />,
  },
  {
    path: '/calculator/seo-traffic-value',
    element: <SeoTrafficValueCalculatorPage />,
  },
  {
    path: '/calculator/review-generation-roi',
    element: <ReviewGenerationRoiCalculatorPage />,
  },
  // ===== ChatGPT Ads cluster =====
  // Hub pillar
  {
    path: '/flywheel/:slug',
    element: <FlywheelPage />,
  },
  {
    path: '/chatgpt-ads-south-florida-home-services',
    element: <ClusterHubPage />,
  },
  // Vertical spokes (6) — component reads slug from pathname
  ...[
    'roofers',
    'hvac-contractors',
    'plumbers',
    'electricians',
    'garage-door-companies',
    'restoration-companies',
  ].map((v) => ({
    path: `/chatgpt-ads-for-${v}-florida`,
    element: <ClusterVerticalPage />,
  })),
  // Location spokes (3)
  ...['miami', 'fort-lauderdale', 'palm-beach'].map((c) => ({
    path: `/chatgpt-ads-${c}`,
    element: <ClusterLocationPage />,
  })),
  // Content/sub-hub spokes (cost, setup, GEO, voice, etc.) — :slug resolves against registry
  ...[
    'chatgpt-ads-cost-setup',
    'geo-get-recommended-by-chatgpt',
    'ai-marketing-florida-contractors',
    'how-much-do-chatgpt-ads-cost',
    'how-to-set-up-chatgpt-ads',
    'chatgpt-ads-vs-google-ads-contractors',
    'get-my-business-recommended-by-chatgpt',
    'what-is-generative-engine-optimization',
    'geo-for-florida-contractors',
    'ai-receptionist-florida-contractors',
    'ai-voice-agent-hvac',
    'ai-lead-generation-roofers',
  ].map((slug) => ({
    path: `/${slug}`,
    element: <ClusterContentPage />,
  })),
  // Blog posts
  {
    path: '/blog/:slug',
    element: <ClusterBlogPage />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;